// moment je drahé v horké cestě – použijeme nativní časové pomocníky
import { OeeBatchRepository } from '@/libs/tpm-analytics/data/oee-batch.repository';
import {
    buildUnionIntervalsByWorkplace,
    sumOverlapMinutes,
    computeIntervalIntersectionMinutes,
} from '@/libs/tpm-analytics/core/downtime.utils';
import { buildCycleTimeResolver, buildLocationChainsForShifts } from '@/libs/tpm-analytics/core/cycle-time.resolver';
import { OeeDailyPoint, OeeSummaryFilters } from '@/libs/tpm-analytics/core/types';
import { computeOeeComponents } from '@/libs/tpm-analytics/core/oee.calculator';
import { reportMissingCycleTime, resetMissingCycleTimes } from '@/libs/tpm-analytics/core/missing-cycle-times';
import { productionDayKey } from '@/libs/tpm-analytics/core/production-day.utils';

export async function getOeeDaily(filters: OeeSummaryFilters): Promise<OeeDailyPoint[]> {
    // POZNÁMKA: resetMissingCycleTimes() se volá v komponentě na začátku load(), ne zde
    const { shifts, unplanned, planned } = await OeeBatchRepository.fetchCurrent(filters.from, filters.to, {
        locationIds: filters.locationIds || (filters.locationId ? [filters.locationId] : []),
        workplaceIds: filters.workplaceIds || (filters.workplaceId ? [filters.workplaceId] : []),
        projectIds: filters.projectIds,
        shiftTypeIds: filters.shiftTypeIds,
        shiftTeamTypeIds: filters.shiftTeamTypeIds,
    });

    // Připrav resolver pro CycleTime s prioritami 1..5 a předpočítej location chainy
    const resolveCt = await buildCycleTimeResolver(shifts);
    const locChainsByWp = buildLocationChainsForShifts(shifts);

    // Akumulační mapy dle zvolené granulariy (day/week/month)
    // DŮLEŽITÉ: Akumulujeme SUROVÁ DATA (OK, NOK, minuty), NE procenta!
    const gran: 'day' | 'week' | 'month' = (filters.granularity as any) || 'day';
    const acc = new Map<
        string,
        {
            totalOk: number;
            totalNok: number;
            totalXMinutes: number;
            totalUnplannedMinutes: number;
            totalIdealRuntimeMinutes: number;
        }
    >();

    // Předpočítej sjednocené intervaly odstávek pro celý rozsah
    const plannedUnion = buildUnionIntervalsByWorkplace(planned as any[], filters.from, filters.to);
    const unplannedUnion = buildUnionIntervalsByWorkplace(unplanned as any[], filters.from, filters.to);

    // Pomocné generátory klíčů - používáme productionDayKey pro správné přiřazení k výrobnímu dni
    function dayKey(ms: number): string {
        return productionDayKey(ms);
    }
    function monthKey(ms: number): string {
        const d = new Date(ms);
        const m = d.getMonth() + 1;
        return `${d.getFullYear()}-${m < 10 ? '0' + m : m}-01`;
    }
    function isoWeekStartKey(ms: number): string {
        const d = new Date(ms);
        const day = (d.getDay() + 6) % 7; // Po=0
        const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
        const m = start.getMonth() + 1;
        const dd = start.getDate();
        return `${start.getFullYear()}-${m < 10 ? '0' + m : m}-${dd < 10 ? '0' + dd : dd}`;
    }

    const rangeFromMs = Date.parse(filters.from);
    const rangeToMs = Date.parse(filters.to);
    for (const s of shifts) {
        if (!s?.fromDate) continue;
        const wid: string | null = (s as any).workplaceId ?? s.workplaceId ?? null;
        const sFromMs = Date.parse(s.fromDate as string);
        const sToMs = Date.parse((s.toDate as string) ?? (s.fromDate as string));
        if (!isFinite(sFromMs) || !isFinite(sToMs) || !isFinite(rangeFromMs) || !isFinite(rangeToMs)) continue;
        const fromIso = new Date(Math.max(rangeFromMs, sFromMs)).toISOString();
        const toIso = new Date(Math.min(rangeToMs, sToMs)).toISOString();
        const shiftMinutes = (() => {
            const a = Date.parse(fromIso),
                b = Date.parse(toIso);
            return b > a ? Math.floor((b - a) / 60000) : 0;
        })();
        const plannedMin = sumOverlapMinutes(plannedUnion.get(wid), Date.parse(fromIso), Date.parse(toIso));
        let unplannedMin = sumOverlapMinutes(unplannedUnion.get(wid), Date.parse(fromIso), Date.parse(toIso));
        // Odečteme překryv plánovaných a neplánovaných odstávek
        const overlapMin = computeIntervalIntersectionMinutes(
            plannedUnion.get(wid),
            unplannedUnion.get(wid),
            Date.parse(fromIso),
            Date.parse(toIso)
        );
        unplannedMin = Math.max(0, unplannedMin - overlapMin);
        const X = Math.max(0, shiftMinutes - plannedMin);

        // Sečti ideální runtime přes všechny produkty ve směně
        const prods: any[] = Array.isArray((s as any)?.shiftProductions) ? ((s as any).shiftProductions as any[]) : [];
        const locChain = locChainsByWp.get(wid) || [];
        let idealRuntimeMinutes = 0;
        for (const p of prods) {
            const pid: string | null = (p as any)?.productId ?? (p as any)?.product?.id ?? null;
            const pcode: string | null = (p as any)?.product?.code ?? null;
            const pname: string | null = (p as any)?.product?.name ?? null;
            const pcid: string | null = (p as any)?.product?.productCollectionId ?? null;
            const pcname: string | null = (p as any)?.product?.productCollectionName ?? null;
            const count = (Number((p as any)?.okCount ?? 0) || 0) + (Number((p as any)?.nokCount ?? 0) || 0);
            if (!pid || count <= 0) continue;
            
            const wpCode: string | null = (s as any)?.workplace?.code ?? null;
            const wpName: string | null = (s as any)?.workplace?.name ?? null;
            const res = resolveCt({ 
                workplaceId: wid, 
                workplaceCode: wpCode,
                workplaceName: wpName,
                productId: pid, 
                productCode: pcode,
                productName: pname,
                productCollectionId: pcid,
                productCollectionName: pcname,
                locationChain: locChain 
            });
            const ctMin = res.minutes;
            if (typeof ctMin === 'number' && ctMin > 0) {
                idealRuntimeMinutes += count * ctMin;
            } else {
                // report ONLY when no product-level match was found (types 1..4 missing)
                if (res.matchType == null) {
                    reportMissingCycleTime({
                        productId: pid,
                        productCode: pcode,
                        count,
                        workplaceCodes: wpCode ? [wpCode] : undefined,
                    });
                }
            }
        }

        // Omezíme idealRuntimeMinutes na max dostupný čas pro výrobu (X - neplánované odstávky)
        // aby nedošlo k přetečení do další směny při špatně nastavené normě
        const maxAvailableMinutes = Math.max(0, X - unplannedMin);
        idealRuntimeMinutes = Math.min(idealRuntimeMinutes, maxAvailableMinutes);

        const ok = Number(s.okCount ?? 0) || 0;
        const nok = Number(s.nokCount ?? 0) || 0;

        // Určíme klíč pro agregaci (den/týden/měsíc)
        let key: string;
        const sStartMs = Date.parse(s.fromDate as string);
        if (gran === 'week') key = isoWeekStartKey(sStartMs);
        else if (gran === 'month') key = monthKey(sStartMs);
        else key = dayKey(sStartMs);

        // AKUMULUJEME surová data (ne procenta!)
        if (!acc.has(key)) {
            acc.set(key, {
                totalOk: 0,
                totalNok: 0,
                totalXMinutes: 0,
                totalUnplannedMinutes: 0,
                totalIdealRuntimeMinutes: 0,
            });
        }
        const a = acc.get(key)!;
        a.totalOk += ok;
        a.totalNok += nok;
        a.totalXMinutes += X;
        a.totalUnplannedMinutes += unplannedMin;
        a.totalIdealRuntimeMinutes += idealRuntimeMinutes;
    }

    console.log('acc', acc);

    // Teprve TEĎ spočítáme OEE procenta z agregovaných dat pro každý den/týden/měsíc
    const result: OeeDailyPoint[] = Array.from(acc.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([day, vals]) => {
            const comp = computeOeeComponents(
                vals.totalOk,
                vals.totalNok,
                0, // planByCt - nepoužíváme
                vals.totalXMinutes,
                vals.totalUnplannedMinutes,
                vals.totalIdealRuntimeMinutes
            );
            return {
                day,
                oee: comp.oee,
                availability: comp.availability,
                performance: comp.performance,
                quality: comp.quality,
            };
        });

    return result;
}
